/**
 * Permission utilities for checking user permissions
 */

export enum PermissionType {
  // Property permissions
  PROPERTY_CREATE = 'property:create',
  PROPERTY_READ = 'property:read',
  PROPERTY_UPDATE = 'property:update',
  PROPERTY_DELETE = 'property:delete',
  PROPERTY_APPROVE = 'property:approve',
  PROPERTY_REJECT = 'property:reject',

  // Client permissions
  CLIENT_CREATE = 'client:create',
  CLIENT_READ = 'client:read',
  CLIENT_UPDATE = 'client:update',
  CLIENT_DELETE = 'client:delete',

  // Agent permissions
  AGENT_CREATE = 'agent:create',
  AGENT_READ = 'agent:read',
  AGENT_UPDATE = 'agent:update',
  AGENT_DELETE = 'agent:delete',

  // Project permissions
  PROJECT_CREATE = 'project:create',
  PROJECT_READ = 'project:read',
  PROJECT_UPDATE = 'project:update',
  PROJECT_DELETE = 'project:delete',
}

/**
 * Check if user has a specific permission
 * @param userPermissions - Array of permissions from user object
 * @param permission - Permission to check (can be string or PermissionType)
 * @returns boolean
 */
export const hasPermission = (userPermissions: string[], permission: string | PermissionType): boolean => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return userPermissions.includes(permission);
};

/**
 * Check if user has all specified permissions
 * @param userPermissions - Array of permissions from user object
 * @param permissions - Array of permissions to check
 * @returns boolean
 */
export const hasAllPermissions = (userPermissions: string[], permissions: (string | PermissionType)[]): boolean => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return permissions.every((permission) => userPermissions.includes(permission));
};

/**
 * Check if user has any of the specified permissions
 * @param userPermissions - Array of permissions from user object
 * @param permissions - Array of permissions to check
 * @returns boolean
 */
export const hasAnyPermission = (userPermissions: string[], permissions: (string | PermissionType)[]): boolean => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return permissions.some((permission) => userPermissions.includes(permission));
};

/**
 * Get permission status for property actions
 */
export const getPropertyPermissions = (userPermissions: string[]) => {
  return {
    canCreate: hasPermission(userPermissions, PermissionType.PROPERTY_CREATE),
    canRead: hasPermission(userPermissions, PermissionType.PROPERTY_READ),
    canUpdate: hasPermission(userPermissions, PermissionType.PROPERTY_UPDATE),
    canDelete: hasPermission(userPermissions, PermissionType.PROPERTY_DELETE),
    canApprove: hasPermission(userPermissions, PermissionType.PROPERTY_APPROVE),
    canReject: hasPermission(userPermissions, PermissionType.PROPERTY_REJECT),
  };
};

/**
 * Get permission status for client actions
 */
export const getClientPermissions = (userPermissions: string[]) => {
  return {
    canCreate: hasPermission(userPermissions, PermissionType.CLIENT_CREATE),
    canRead: hasPermission(userPermissions, PermissionType.CLIENT_READ),
    canUpdate: hasPermission(userPermissions, PermissionType.CLIENT_UPDATE),
    canDelete: hasPermission(userPermissions, PermissionType.CLIENT_DELETE),
  };
};

/**
 * Get permission status for agent actions
 */
export const getAgentPermissions = (userPermissions: string[]) => {
  return {
    canCreate: hasPermission(userPermissions, PermissionType.AGENT_CREATE),
    canRead: hasPermission(userPermissions, PermissionType.AGENT_READ),
    canUpdate: hasPermission(userPermissions, PermissionType.AGENT_UPDATE),
    canDelete: hasPermission(userPermissions, PermissionType.AGENT_DELETE),
  };
};

/**
 * Get permission status for project actions
 */
export const getProjectPermissions = (userPermissions: string[]) => {
  return {
    canCreate: hasPermission(userPermissions, PermissionType.PROJECT_CREATE),
    canRead: hasPermission(userPermissions, PermissionType.PROJECT_READ),
    canUpdate: hasPermission(userPermissions, PermissionType.PROJECT_UPDATE),
    canDelete: hasPermission(userPermissions, PermissionType.PROJECT_DELETE),
  };
};
